/**
 * Day-of-week chip picker. Mon-first, toggles bits in a weekday mask.
 * Three shortcut buttons: Mon–Fri, weekend, clear.
 */
import {
  maskHas,
  toggleDay,
  WEEKDAYS_MON_FIRST,
  WEEKDAY_SHORT_KEYS,
  WEEKDAY_MASK_WEEKDAYS,
  WEEKDAY_MASK_WEEKENDS,
  type WeekdayIndex,
} from '@waylo/shared';
import { useT } from '../lib/i18n';
import { hapticSelection } from '../lib/telegram';
import { cn } from '../lib/cn';

interface Props {
  mask: number;
  onChange: (mask: number) => void;
}

export function WeekdayPicker({ mask, onChange }: Props): JSX.Element {
  const { t } = useT();
  const toggle = (next: number): void => {
    hapticSelection();
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-1.5">
        {WEEKDAYS_MON_FIRST.map((day) => {
          const active = maskHas(mask, day as WeekdayIndex);
          return (
            <button
              key={day}
              type="button"
              onClick={() => toggle(toggleDay(mask, day as WeekdayIndex))}
              className={cn(
                'flex-1 rounded-input border-2 py-2.5 text-xs font-semibold uppercase tracking-wide transition',
                active
                  ? 'border-transparent bg-[var(--border-medium)] font-bold text-tg-text'
                  : 'border-[var(--color-input-border)] bg-[var(--color-input-bg)] text-tg-hint',
              )}
            >
              {t(`weekday.${WEEKDAY_SHORT_KEYS[day]}` as 'weekday.mon')}
            </button>
          );
        })}
      </div>
      <div className="flex gap-2 text-xs">
        <button
          type="button"
          onClick={() => toggle(WEEKDAY_MASK_WEEKDAYS)}
          className="rounded-pill bg-tg-bg-secondary px-3 py-1.5 font-medium text-tg-link"
        >
          {t('publish.step.repeat.allWeekdays')}
        </button>
        <button
          type="button"
          onClick={() => toggle(WEEKDAY_MASK_WEEKENDS)}
          className="rounded-pill bg-tg-bg-secondary px-3 py-1.5 font-medium text-tg-link"
        >
          {t('publish.step.repeat.weekend')}
        </button>
      </div>
    </div>
  );
}
